/**
 * Animals & pens — the husbandry panel.
 *
 * The system is a CONVERTER, not a faucet, and the panel has to say so or the
 * player buys a 500-coin cow and watches it produce nothing: an animal only
 * accrues while it is FED, feed is wheat out of your own bag, and stock arrives
 * hungry. So every row leads with hunger, and the buy button warns before it
 * spends.
 *
 * Everything shown here is derived server-side on read (pending produce, hunger),
 * which is why each action re-renders from the `husbandry` snapshot the response
 * carries rather than patching numbers in place.
 */
import { el, toast } from '../../dom';
import { villageApi, type Husbandry, type FarmAnimal, type Pen, type LivestockType } from '../../game-api';
import { ApiError } from '../../../core/network/api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { BUILDINGS, closePlanter, openOverlay, refreshBag, type FarmCtx } from './context';
import { openConfirm } from './confirm-dialog';
import { modalWrap } from '../../ui/panel';
import { richText, gicon } from '../../ui/icons';
import { refreshLivestock } from './livestock';

/** Pens with a free slot that accept this species — where a purchase can go. */
export function pensFor(species: LivestockType, hus: Husbandry): Pen[] {
  return hus.pens.filter((p) => p.accepts.includes(species.species) && p.used < p.slots);
}

/** A pen tile the panel was opened from, so a purchase lands in THAT building. */
export interface PenRef { tx: number; ty: number }

/**
 * Building kinds that are pens.
 *
 * Mirrors PENS in backend/src/game/village/husbandry.config.ts — the client has to
 * decide whether to offer the animals row on a tap WITHOUT a round trip, and the
 * building menu is drawn synchronously. A backend test pins the server list
 * against these two names so adding a third pen cannot silently ship a building
 * that houses animals but never offers them.
 */
export const PEN_KINDS = new Set(['coop', 'barn']);
export const isPenBuilding = (kind: string): boolean => PEN_KINDS.has(kind);

/**
 * Which pen a Buy click should fill.
 *
 * When the panel was opened by tapping a coop, buying a chicken must put it in
 * THAT coop — before this the panel always took `pensFor(...)[0]`, i.e. the
 * lowest-coordinate pen on the farm, so a player with two coops tapped one and
 * got a chicken in the other. Falls back to the first pen with room when the
 * tapped building cannot take this species (a barn cannot hold a chicken).
 */
export function preferredPen(sp: LivestockType, hus: Husbandry, focus?: PenRef | null): Pen | undefined {
  const open = pensFor(sp, hus);
  return open.find((p) => focus && p.tx === focus.tx && p.ty === focus.ty) ?? open[0];
}

/** "Hungry" / "fed for 3h 20m" — the single fact that decides whether it earns. */
export function fedLabel(a: FarmAnimal, now = Date.now()): string {
  if (a.hungry || !a.fedUntil) return 'hungry — earning nothing';
  const ms = new Date(a.fedUntil).getTime() - now;
  if (ms <= 0) return 'hungry — earning nothing';
  const h = Math.floor(ms / 3_600_000), m = Math.floor((ms % 3_600_000) / 60_000);
  return `fed for ${h > 0 ? `${h}h ` : ''}${m}m`;
}

/**
 * `focus` is the pen the player tapped, when the panel was opened from a coop or
 * barn rather than from the Village panel. It decides where a purchase lands and
 * which pen row is called out.
 */
export function openHusbandry(c: FarmCtx, focus?: PenRef | null) {
  void dispatchAction('ui.husbandry', async () => {
    render(c, await villageApi.husbandry(), focus ?? null);
  }).catch((e) => { if (!isDropped(e)) toast('Could not open the pens', 'error'); });
}

function itemName(hus: Husbandry, id: string): string {
  return hus.itemNames[id] ?? id;
}

function render(c: FarmCtx, hus: Husbandry, focus: PenRef | null = null) {
  const pendingTotal = hus.animals.reduce((n, a) => n + a.pending, 0);
  const anyHungry = hus.animals.some((a) => a.hungry);

  const card = el('div', 'bg-surface border border-border rounded-lg p-4 w-[460px] max-w-[92vw] max-h-[86vh] overflow-y-auto shadow-lift sheet-card', {},
    el('div', 'flex justify-between items-center mb-1', {},
      el('h3', 'font-heading text-gold', {}, 'Animals & pens'),
      el('button', 'tb-close-x text-text-dim hover:text-text text-lg', { onClick: () => closePlanter(c) }, '×')),
    el('div', 'text-[11px] text-text-dim mb-3', {},
      `${hus.count}/${hus.maxAnimals} animals. They only produce while fed — feed comes out of your own bag.`));

  // ── Pens ────────────────────────────────────────────────────────────────────
  card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mb-1', {}, 'Pens'));
  if (!hus.pens.length) {
    card.append(el('div', 'text-[12px] text-text-dim mb-3', {},
      'No finished pens. Build a Chicken Coop (Village, pop 6) or a Barn (Town, pop 11) — scaffolding cannot house anything.'));
  }
  for (const p of hus.pens) {
    // The pen you tapped is called out, because the panel lists every pen on the
    // farm and otherwise nothing tied it back to the building under your finger.
    const here = !!focus && p.tx === focus.tx && p.ty === focus.ty;
    card.append(el('div', `flex items-center gap-3 p-2 mb-1.5 rounded border ${here ? 'border-gold' : 'border-border'}`, {},
      el('img', 'w-8 h-8 object-contain', { src: `/sprites/buildings/${p.kind}.png`, style: 'image-rendering:pixelated' }),
      el('div', 'flex-1 min-w-0', {},
        el('div', 'text-sm', {}, here ? `${p.name} (this one)` : p.name),
        el('div', 'text-[11px] text-text-dim font-mono', {}, `at ${p.tx}, ${p.ty} · ${p.used}/${p.slots} · takes ${p.accepts.join(', ')}`))));
  }

  // ── Stock ───────────────────────────────────────────────────────────────────
  card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mt-2 mb-1', {}, 'Your stock'));
  if (!hus.animals.length) card.append(el('div', 'text-[12px] text-text-dim mb-2', {}, 'No animals yet — buy some below.'));
  for (const a of hus.animals) {
    card.append(el('div', 'flex items-center gap-3 p-2 mb-1.5 rounded border border-border', {},
      el('div', 'flex-1 min-w-0', {},
        el('div', 'text-sm', {}, `${a.name} · ${a.species}`),
        el('div', `text-[11px] font-mono ${a.hungry ? 'text-error' : 'text-text-dim'}`, {}, fedLabel(a)),
        el('div', 'text-[11px] text-text-dim font-mono', {},
          `${a.pending}/${a.maxPending} ${itemName(hus, a.produces)} · ${a.feedQty} ${itemName(hus, a.feedItem)} per feed`)),
      // Release banks whatever it earned but refunds no coins — same rule as
      // demolishing a building, so it is confirmed like one.
      el('button', 'text-text-dim hover:text-danger text-sm px-1', {
        title: `Let ${a.name} go (no refund)`,
        // Painted card, not window.confirm() — see confirm-dialog.ts.
        onClick: () => openConfirm({
          title: `Let ${a.name} go?`,
          body: 'It leaves the pen for good.',
          detail: 'Anything it produced is banked first, but the coins it cost are gone.',
          confirmLabel: `Release ${a.name}`,
          cancelLabel: 'Keep it',
          onConfirm: () => void doRelease(c, a.id),
        }),
      }, gicon('xmark', 11))));
  }

  if (hus.animals.length) {
    card.append(el('button',
      `w-full mt-1 mb-1.5 px-3 py-2 rounded border text-sm ${anyHungry ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
      { onClick: anyHungry ? () => void doFeed(c) : undefined },
      anyHungry ? 'Feed everyone (uses your wheat)' : 'Everyone is fed'));
    card.append(el('button',
      `w-full mb-3 px-3 py-2 rounded border text-sm ${pendingTotal > 0 ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
      { onClick: pendingTotal > 0 ? () => void doCollect(c) : undefined }, `Collect produce (${pendingTotal})`));
  }

  // ── Buy ─────────────────────────────────────────────────────────────────────
  card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mt-2 mb-1', {}, 'Buy stock'));
  const full = hus.count >= hus.maxAnimals;
  for (const sp of hus.species) {
    const target = preferredPen(sp, hus, focus);
    const can = !full && !!target;
    card.append(el('div', 'flex items-center gap-3 p-2 mb-1.5 rounded border border-border', {},
      el('div', 'flex-1 min-w-0', {},
        el('div', 'text-sm', {}, sp.name),
        el('div', 'text-[11px] text-text-dim', {},
          `${sp.ratePerHour} ${sp.produceName}/h while fed · ${sp.feedQty} ${itemName(hus, sp.feedItem)} buys ${sp.fedHours}h`),
        el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1', {},
          ...richText(`🪙 ${sp.costCoins} · lives in the ${BUILDINGS[sp.pen]?.name ?? sp.pen}`))),
      el('button',
        `px-2.5 py-1 rounded border text-sm shrink-0 ${can ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
        { onClick: can && target ? () => void doBuy(c, sp, target, focus) : undefined },
        full ? 'Farm full' : target ? 'Buy' : 'No pen')));
  }

  closePlanter(c);
  openOverlay(c, modalWrap(() => closePlanter(c), card));
}

/** Re-render, re-seed the pen sprites, and re-read anything the call moved. */
async function after(c: FarmCtx, hus: Husbandry, opts: { currency?: boolean; focus?: PenRef | null } = {}) {
  render(c, hus, opts.focus ?? null);
  void refreshLivestock(c, hus.animals);
  if (opts.currency) await c.game.refreshHud();
  refreshBag(c);
}

function doBuy(c: FarmCtx, sp: LivestockType, pen: Pen, focus: PenRef | null = null): void {
  // Stock arrives HUNGRY and produces nothing until fed. Saying so before the
  // coins move is the difference between a purchase and a complaint.
  //
  // Painted card, not window.confirm() (owner: "we make ingame confirmation, not
  // via chrome") — which also means the answer arrives in a callback, so the
  // purchase moved to the other side of it.
  openConfirm({
    title: `Buy a ${sp.name}?`,
    body: `${sp.costCoins} coins, into the ${pen.name} at ${pen.tx}, ${pen.ty}.`,
    detail: `It arrives hungry — it earns nothing until you feed it ${sp.feedQty} wheat.`,
    confirmLabel: `Buy the ${sp.name}`,
    cancelLabel: 'Not now',
    onConfirm: () => void buyNow(c, sp, pen, focus),
  });
}

async function buyNow(c: FarmCtx, sp: LivestockType, pen: Pen, focus: PenRef | null): Promise<void> {
  try {
    await dispatchAction('husbandry.buy', async (actionId) => {
      const r = await villageApi.buyAnimal(sp.species, pen.tx, pen.ty, actionId);
      toast(`${r.name} moved into the ${pen.name} — feed it to get started`);
      await after(c, r.husbandry, { currency: true, focus });
    }, { key: `${sp.species}:${pen.tx},${pen.ty}` });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not buy the animal', 'error');
  }
}

async function doFeed(c: FarmCtx) {
  try {
    await dispatchAction('husbandry.feed', async (actionId) => {
      // No ids: feed everything the bag can pay for, which is what the button says.
      const r = await villageApi.feedAnimals(undefined, actionId);
      const banked = Object.values(r.collected).reduce((n, q) => n + q, 0);
      toast(banked > 0 ? `Fed ${r.fed.length} · banked ${banked} produce` : `Fed ${r.fed.length}`);
      await after(c, r.husbandry);
    });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not feed them', 'error');
  }
}

async function doCollect(c: FarmCtx) {
  try {
    await dispatchAction('husbandry.collect', async (actionId) => {
      const r = await villageApi.collectProduce(actionId);
      const total = Object.values(r.granted).reduce((n, q) => n + q, 0);
      toast(total > 0 ? `Collected ${total}` : 'Nothing ready yet');
      await after(c, r.husbandry);
    });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Collect failed', 'error');
  }
}

async function doRelease(c: FarmCtx, animalId: string) {
  try {
    await dispatchAction('husbandry.release', async (actionId) => {
      const r = await villageApi.releaseAnimal(animalId, actionId);
      toast(r.banked > 0 ? `Let go — banked ${r.banked}` : 'Let go');
      await after(c, r.husbandry);
    }, { key: animalId });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not release it', 'error');
  }
}
